/**
 * M4 Map Render & Style — public surface. The renderer + UI compose from these;
 * the pure engine (types, spec, colors, expressions, style) is browser-safe and
 * test-covered. See docs/build/M4.md.
 */

// PointSet + GeoJSON
export {
  buildPointSet,
  columnsFromProfiles,
  boundsOf,
  toGeoJSON,
  type PointSet,
  type Point,
  type PointColumn,
  type PointColumnType,
  type Bounds,
  type ResolvedCoord,
  type PointFeature,
  type PointFeatureCollection,
} from "./pointset";
export { coordinateModeResolver, geocodeResultResolver } from "./resolve";

// Point-level editing (MAP-7) — add/remove an individual point in the PointSet.
export {
  synthesizePoint,
  addPointToSet,
  removePointFromSet,
  applyManualEdits,
  canAddPoint,
  placeLabel,
  type SynthesizePointArgs,
  type AddGuard,
} from "./pointEdit";

// MapSpec
export {
  MAPSPEC_VERSION,
  CLUSTER_LEGIBILITY_THRESHOLD,
  DEFAULT_CLUSTER_RADIUS,
  DEFAULT_CLUSTER_MAX_ZOOM,
  MapSpecSchema,
  parseMapSpec,
  safeParseMapSpec,
  migrateMapSpec,
  defaultSpec,
  MARKER_SHAPES,
  MARKER_SIZES,
  DEFAULT_MARKER,
  type MapSpec,
  type FilterClause,
  type FilterOp,
  type ColorBy,
  type SizeBy,
  type IconBy,
  type MarkerShape,
  type MarkerSize,
  type MarkerSpec,
} from "./mapspec";

// Analysis spec sublayer (M8) — the optional `analysis` block on MapSpec.
export {
  ANALYSIS_VERSION,
  AnalysisSpecSchema,
  emptyAnalysis,
  migrateAnalysis,
  type AnalysisSpec,
  type Territories,
  type Territory,
  type TerritoryMode,
  type TerritoryRollup,
  type RadiusCircle,
  type RadiusCenter,
  type BoundaryKind,
  type BoundaryRef,
} from "@/lib/analysis/spec";

// Overlay spec sublayer (M9) — the optional `overlay` block on MapSpec.
export {
  OVERLAY_VERSION,
  OverlaySpecSchema,
  migrateOverlay,
  type OverlaySpec,
  type OverlayClassification,
} from "@/lib/overlay/spec";

// Colors + expressions + legend
export { categoryColoring, numericBreaks, type CategoryColoring, type CategoryEntry, type BreakMethod } from "./color";
// Shapes (MAP-24 shape-by) — deterministic value→shape assignment
export {
  SHAPE_BY_CYCLE,
  SHAPE_BY_OTHER,
  shapeAssignment,
  type ShapeEntry,
  type ShapeByAssignment,
} from "./shape";
export {
  colorExpression,
  sizeExpression,
  filterExpression,
  clauseExpression,
  legendModel,
  shapeLegendModel,
  legendsMerge,
  categoricalColorExpression,
  numericColorExpression,
  type MapExpr,
  type LegendModel,
  type ShapeLegendModel,
} from "./expression";

// Tokens + base style
export { getMapTokens, labelFontstack, DEFAULT_MAP_THEME, type MapTokens } from "./mapTokens";
export {
  buildMapStyle,
  styleFromDescriptor,
  GLYPH_FONTSTACK_REGULAR,
  GLYPH_FONTSTACK_BOLD,
  BASEMAPS,
  BASEMAP_LABELS,
  PAID_BASEMAPS,
  FREE_BASEMAP_FALLBACK,
  normalizeBasemap,
  isPaidBasemap,
  clampBasemapForEntitlement,
  type MapStyle,
  type Basemap,
  type BasemapVariant,
  type TileDescriptor,
} from "./style";
